import {
  Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren
} from '@angular/core';

export const TOTP_CODE_LENGTH = 6;

/**
 * Pole na 6-cyfrowy kod TOTP: jedno okienko na cyfrę, auto-przejście do
 * następnego, obsługa Backspace, strzałek i wklejenia całego kodu.
 * Odpowiednik shadcn InputOTP z Checkapp.
 */
@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html'
})
export class OtpInputComponent implements OnChanges {
  @Input() value = '';
  @Input() disabled = false;
  @Input() autofocus = false;
  @Output() valueChange = new EventEmitter<string>();
  /** Emitowane po wpisaniu wszystkich cyfr – pozwala wysłać formularz bez klikania. */
  @Output() completed = new EventEmitter<string>();

  readonly slots = Array.from({ length: TOTP_CODE_LENGTH }, (_, i) => i);
  digits: string[] = this.emptyDigits();

  @ViewChildren('digitInput') private inputs: QueryList<ElementRef<HTMLInputElement>>;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value) {
      const next = (this.value || '').replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH);
      this.digits = this.emptyDigits().map((_, i) => next[i] || '');
    }
  }

  onInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const typed = input.value.replace(/\D/g, '');

    // Wklejenie / autouzupełnienie całego kodu w jedno pole.
    if (typed.length > 1) {
      this.fill(typed, index);
      return;
    }

    this.digits[index] = typed;
    input.value = typed;
    this.emit();

    if (typed && index < TOTP_CODE_LENGTH - 1) {
      this.focus(index + 1);
    }
  }

  onKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      event.preventDefault();
      this.digits[index - 1] = '';
      this.emit();
      this.focus(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focus(index - 1);
    } else if (event.key === 'ArrowRight' && index < TOTP_CODE_LENGTH - 1) {
      event.preventDefault();
      this.focus(index + 1);
    }
  }

  onPaste(index: number, event: ClipboardEvent) {
    event.preventDefault();
    const pasted = (event.clipboardData ? event.clipboardData.getData('text') : '').replace(/\D/g, '');
    if (pasted) {
      this.fill(pasted, index);
    }
  }

  private fill(digits: string, from: number) {
    const chars = digits.slice(0, TOTP_CODE_LENGTH - from).split('');
    chars.forEach((char, offset) => { this.digits[from + offset] = char; });
    this.emit();
    this.focus(Math.min(from + chars.length, TOTP_CODE_LENGTH - 1));
  }

  private emit() {
    const value = this.digits.join('');
    this.valueChange.emit(value);
    if (value.length === TOTP_CODE_LENGTH) {
      this.completed.emit(value);
    }
  }

  private focus(index: number) {
    const input = this.inputs && this.inputs.toArray()[index];
    if (input) {
      input.nativeElement.focus();
      input.nativeElement.select();
    }
  }

  private emptyDigits(): string[] {
    return Array.from({ length: TOTP_CODE_LENGTH }, () => '');
  }
}
